import type { PrescriptionPrintTemplateId } from '@helios/data-access';

/**
 * System print templates available in v1 (PRD.184 §6.1).
 *
 * Each template defines high-level layout strategy hints that the
 * generated `PrescriptionPrintLayout` renderer applies on top of the
 * clinic's branding + section settings.
 */

export type HeaderAlign = 'left' | 'center' | 'right';
export type Density = 'compact' | 'comfortable';
export type DoctorBlockPosition = 'header_right' | 'header_below_clinic' | 'footer';
export type ItemsLayout = 'table' | 'numbered_list';

export interface SystemTemplate {
  id: PrescriptionPrintTemplateId;
  label: string;
  description: string;

  headerAlign: HeaderAlign;
  showRxSymbol: boolean;
  doctorBlock: DoctorBlockPosition;
  itemsLayout: ItemsLayout;
  density: Density;
  /** Whether the template emphasizes the logo (larger logo treatment in header). */
  prominentLogo: boolean;
}

export const SYSTEM_TEMPLATES: SystemTemplate[] = [
  {
    id: 'classic_clinical',
    label: 'Classic Clinical',
    description:
      'Polished letterhead — clinic on the left, doctor credentials on the right, themed items table with M-A-N dosage cells, signature block, and an iconified 3-column footer.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'clean_classic',
    label: 'Clean Classic',
    description: 'Centered logo + clinic header with the classic Rx symbol and a tidy items table.',
    headerAlign: 'center',
    showRxSymbol: true,
    doctorBlock: 'header_below_clinic',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'compact_modern',
    label: 'Compact Modern',
    description: 'Left-aligned modern header. Dense items table — fits more rows per page.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'compact',
    prominentLogo: false,
  },
  {
    id: 'doctor_letterhead',
    label: 'Doctor Letterhead',
    description:
      'Letterhead-style header with clinic on the left and doctor credentials on the right.',
    headerAlign: 'left',
    showRxSymbol: true,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'minimal_prescription',
    label: 'Minimal Prescription',
    description: 'Stripped-back layout — no Rx symbol, simple numbered list of prescribed items.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'footer',
    itemsLayout: 'numbered_list',
    density: 'compact',
    prominentLogo: false,
  },
  {
    // PRD.284 — Letterhead Classic. Renders via a dedicated layout branch in
    // PrescriptionPrintLayout (two-column doctor/practice header, large writing
    // area, centered footer) for clinics using traditional pre-printed pads.
    id: 'classic_letterhead',
    label: 'Letterhead Classic',
    description:
      'Traditional two-column doctor header with centered footer for pre-printed clinic stationery.',
    headerAlign: 'left',
    showRxSymbol: true,
    doctorBlock: 'header_right',
    itemsLayout: 'numbered_list',
    density: 'comfortable',
    prominentLogo: true,
  },
];

export function getSystemTemplate(id: PrescriptionPrintTemplateId): SystemTemplate {
  return SYSTEM_TEMPLATES.find((t) => t.id === id) ?? SYSTEM_TEMPLATES[0];
}
